import { useEffect, useCallback } from 'react';

type ModifierKey = 'ctrl' | 'alt' | 'shift' | 'meta';
type HotkeyCallback = (e: KeyboardEvent) => void;

interface HotkeyConfig {
  key: string;
  callback: HotkeyCallback;
  modifiers?: ModifierKey[];
}

const isInputElement = (element: Element | null): boolean => {
  if (!element) return false;
  const tagName = element.tagName.toLowerCase();
  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    (element as HTMLElement).contentEditable === 'true'
  );
};

export const useHotkeys = (hotkeys: HotkeyConfig[]) => {
  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger if focus is on input elements
      if (isInputElement(document.activeElement)) {
        return;
      }

      const pressedKey = event.key.toLowerCase();

      for (const hotkey of hotkeys) {
        const targetKey = hotkey.key.toLowerCase();
        const modifiers = hotkey.modifiers || [];

        const modifiersMatch =
          (!modifiers.includes('ctrl') === !event.ctrlKey) &&
          (!modifiers.includes('alt') === !event.altKey) &&
          (!modifiers.includes('shift') === !event.shiftKey) &&
          (!modifiers.includes('meta') === !event.metaKey);

        if (pressedKey === targetKey && modifiersMatch) {
          event.preventDefault();
          hotkey.callback(event);
          break;
        }
      }
    },
    [hotkeys]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);
};
